import * as arrow from "apache-arrow";
import { readParquet, writeParquet, Table } from "parquet-wasm";
import dayjs from "./dayjs";

export type ParsedParquetData = {
  arrowTable: arrow.Table;
  columns: arrow.Field<any>[];
};

export function convertValueToType(value: any, type: arrow.DataType): any {
  if (value == null) return value;
  switch (type.typeId) {
    case arrow.Type.Timestamp: {
      return dayjs.utc(value).toDate();
    }
    case arrow.Type.Date:
      // Arrow Date values are days since epoch, convert to milliseconds
      try {
        const milliseconds = value * 86400000;
        const date = new Date(milliseconds);
        if (isNaN(date.getTime())) {
          console.warn("Invalid Arrow Date conversion:", value, "->", date);
          return value; // Return original value if conversion fails
        }
        return date;
      } catch (error) {
        console.warn("Error converting Arrow Date:", value, error);
        return value; // Return original value if conversion fails
      }
    case arrow.Type.Int:
    case arrow.Type.Int8:
    case arrow.Type.Int16:
    case arrow.Type.Int32:
    case arrow.Type.Int64:
    case arrow.Type.Uint8:
    case arrow.Type.Uint16:
    case arrow.Type.Uint32:
    case arrow.Type.Uint64:
    case arrow.Type.Float:
    case arrow.Type.Float16:
    case arrow.Type.Float32:
    case arrow.Type.Float64:
      return Number(value);
    case arrow.Type.Utf8:
      return value.toString();
    case arrow.Type.Bool:
      return Boolean(value);
    default:
      return value; // fallback: return as-is
  }
}

// Function to parse parquet data
export const parseParquetData = async (
  parquetData: string,
  isBase64: boolean
): Promise<ParsedParquetData> => {
  try {
    let parquetBuffer: Uint8Array;
    if (isBase64) {
      // Decode base64 string
      const binaryString = atob(parquetData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      parquetBuffer = bytes;
    } else {
      // Download from URL
      const response = await fetch(parquetData);
      if (!response.ok) {
        throw new Error(
          `Failed to download parquet file: ${response.statusText}`
        );
      }
      const arrayBuffer = await response.arrayBuffer();
      parquetBuffer = new Uint8Array(arrayBuffer);
    }
    // Parse parquet file using WASM
    const wasmTable = readParquet(parquetBuffer);
    // Convert WASM table to JavaScript Arrow table
    const arrowTable = arrow.tableFromIPC(wasmTable.intoIPCStream());
    return {
      arrowTable,
      columns: arrowTable.schema.fields,
    };
  } catch (error) {
    console.error("Error parsing parquet data:", error);
    throw new Error(
      `Failed to parse parquet data: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
};

/**
 * Converts an array of objects to base64-encoded Parquet format
 * This creates a proper Parquet file with PAR1 footer signature
 *
 * @param data Array of objects where keys are column names and values are cell values
 * @returns Base64-encoded string of the actual Parquet file
 */
export const arrayToParquetBase64 = (data: Record<string, any>[]): string => {
  if (data.length === 0) return "";

  // Get column names from first object
  const columns = Object.keys(data[0]);

  // Prepare column arrays for Arrow
  const columnArrays: Record<string, any[]> = {};
  columns.forEach((col) => {
    columnArrays[col] = data.map((row) => row[col]);
  });

  // Create Arrow table
  const arrowTable = arrow.tableFromArrays(columnArrays);

  // Convert Arrow table to IPC stream
  const arrowBytes = arrow.tableToIPC(arrowTable);

  // Create WASM Table instance from IPC stream
  const wasmTable = Table.fromIPCStream(new Uint8Array(arrowBytes));

  // Use parquet-wasm writeParquet with the WASM Table
  const parquetBuffer = writeParquet(wasmTable);

  // Convert to base64
  return btoa(String.fromCharCode(...new Uint8Array(parquetBuffer)));
};
